import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Palette, Sparkles, Server } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { PageHeader } from "@/components/admin/AdminUI";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const EMPTY_BRAND = {
  product_name: "Pravadh GovernX",
  product_short: "GovernX",
  tagline: "Connect. Clean. Govern.",
  badge: "GX",
  mark_letter: "P",
  primary: "221 83% 53%",
  accent: "37 99% 50%",
  from_name: "Pravadh GovernX",
  assistant_name: "EMIE",
  custom_domain: "",
};

export default function AdminInstance() {
  const [brand, setBrand] = useState(null);
  const [llm, setLlm] = useState(null);
  const [llmForm, setLlmForm] = useState({ provider: "anthropic", base_url: "", model: "", api_key: "" });
  const [savingBrand, setSavingBrand] = useState(false);
  const [savingLlm, setSavingLlm] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = () => {
    api.get("/settings/branding").then((r) => setBrand({ ...EMPTY_BRAND, ...r.data })).catch(() => setBrand(false));
    api.get("/settings/llm").then((r) => {
      setLlm(r.data);
      setLlmForm((f) => ({
        ...f,
        provider: r.data.provider || "anthropic",
        base_url: r.data.base_url || "",
        model: r.data.model || "",
        api_key: "",
      }));
    }).catch(() => setLlm(false));
  };

  useEffect(() => { load(); }, []);

  const saveBrand = async () => {
    setSavingBrand(true);
    try {
      const { data } = await api.put("/settings/branding", brand);
      setBrand({ ...EMPTY_BRAND, ...data });
      toast.success("Branding saved. Refresh the page to apply colors everywhere.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't save branding");
    } finally {
      setSavingBrand(false);
    }
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/settings/branding/logo", fd);
      setBrand({ ...EMPTY_BRAND, ...data });
      toast.success("Logo uploaded");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't upload logo");
    } finally {
      setUploading(false);
    }
  };

  const saveLlm = async () => {
    if (!llmForm.model.trim()) {
      toast.error("Model is required");
      return;
    }
    setSavingLlm(true);
    try {
      const { data } = await api.put("/settings/llm", llmForm);
      setLlm(data);
      setLlmForm((f) => ({ ...f, api_key: "" }));
      toast.success("LLM settings saved");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't save LLM settings");
    } finally {
      setSavingLlm(false);
    }
  };

  if (brand === null || llm === null) {
    return <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  }
  if (brand === false) return <p className="text-sm text-destructive">Failed to load instance settings.</p>;

  return (
    <div className="max-w-2xl space-y-8" data-testid="admin-instance">
      <PageHeader title="White-label" subtitle="Product name, colors, domain, and the model this instance uses for the assistant." />

      <section className="rounded-2xl border border-border bg-card p-5" data-testid="branding-form">
        <div className="mb-4 flex items-center gap-2">
          <Palette className="h-4 w-4 text-primary" />
          <h2 className="font-display text-lg font-bold">Brand</h2>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          {[
            ["product_name", "Product name"],
            ["product_short", "Short name"],
            ["tagline", "Tagline"],
            ["assistant_name", "Assistant name"],
            ["from_name", "Email from name"],
            ["custom_domain", "Customer domain"],
            ["badge", "Logo badge"],
            ["mark_letter", "Mark letter"],
            ["primary", "Primary HSL"],
            ["accent", "Accent HSL"],
          ].map(([key, label]) => (
            <div key={key} className="space-y-1.5">
              <Label htmlFor={`brand-${key}`}>{label}</Label>
              <Input
                id={`brand-${key}`}
                value={brand[key] || ""}
                onChange={(e) => setBrand((b) => ({ ...b, [key]: e.target.value }))}
                data-testid={`brand-${key}`}
              />
            </div>
          ))}
        </div>
        <div className="mt-4 space-y-1.5">
          <Label htmlFor="brand-logo">Logo</Label>
          <Input
            id="brand-logo"
            type="file"
            accept="image/png,image/jpeg,image/webp,image/svg+xml"
            onChange={(e) => uploadLogo(e.target.files?.[0])}
            disabled={uploading}
            data-testid="brand-logo"
          />
          {brand.logo_url && <p className="text-xs text-muted-foreground">Current logo is stored on this instance.</p>}
        </div>
        <Button className="mt-5 font-semibold" onClick={saveBrand} disabled={savingBrand} data-testid="brand-save">
          {savingBrand ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save branding"}
        </Button>
      </section>

      <section className="rounded-2xl border border-border bg-card p-5" data-testid="llm-form">
        <div className="mb-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-primary" />
            <h2 className="font-display text-lg font-bold">Own LLM</h2>
          </div>
          <Badge variant={llm?.configured ? "default" : "secondary"}>
            {llm?.configured ? (llm.from_tenant ? `Tenant key ···${llm.last4}` : `HQ key ···${llm.last4}`) : "Not configured"}
          </Badge>
        </div>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>Provider</Label>
            <Select value={llmForm.provider} onValueChange={(v) => setLlmForm((f) => ({ ...f, provider: v }))}>
              <SelectTrigger data-testid="llm-provider"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="anthropic">Anthropic (Claude)</SelectItem>
                <SelectItem value="openai">OpenAI</SelectItem>
                <SelectItem value="openai_compatible">OpenAI-compatible (Azure, Groq, Ollama, Cursor-compatible gateways)</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Bring any chat-completions API. Cursor itself is an editor, not a hosted model — paste a compatible base URL if you proxy through one. The key never goes into EMIE prompts, and the assistant cannot change billing or subscription dates.
            </p>
          </div>
          {llmForm.provider !== "anthropic" && (
            <div className="space-y-1.5">
              <Label htmlFor="llm-base">Base URL</Label>
              <Input
                id="llm-base"
                placeholder="https://api.openai.com/v1"
                value={llmForm.base_url}
                onChange={(e) => setLlmForm((f) => ({ ...f, base_url: e.target.value }))}
                data-testid="llm-base"
              />
            </div>
          )}
          <div className="space-y-1.5">
            <Label htmlFor="llm-model">Model</Label>
            <Input
              id="llm-model"
              value={llmForm.model}
              onChange={(e) => setLlmForm((f) => ({ ...f, model: e.target.value }))}
              placeholder="claude-sonnet-4-6"
              data-testid="llm-model"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="llm-key">API key</Label>
            <Input
              id="llm-key"
              type="password"
              autoComplete="off"
              value={llmForm.api_key}
              onChange={(e) => setLlmForm((f) => ({ ...f, api_key: e.target.value }))}
              placeholder={llm?.configured ? "Leave blank to keep the current key" : "Paste the tenant key"}
              data-testid="llm-key"
            />
          </div>
        </div>
        <Button className="mt-5 font-semibold" onClick={saveLlm} disabled={savingLlm} data-testid="llm-save">
          {savingLlm ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save LLM"}
        </Button>
      </section>

      <section className="rounded-2xl border border-border bg-card p-5" data-testid="instance-move-server">
        <div className="mb-3 flex items-center gap-2">
          <Server className="h-4 w-4 text-primary" />
          <h2 className="font-display text-lg font-bold">Move this instance</h2>
        </div>
        <p className="text-sm text-muted-foreground">
          Put these same images on another VM or cluster you own. Copy only this instance&apos;s LICENSE_* and Mongo volume — never Pravadh HQ keys. Generate new JWT and Fernet if the database is new.
        </p>
        <Link to="/deploy" className="mt-4 inline-block">
          <Button variant="outline" className="font-semibold" data-testid="instance-open-deploy-guide">
            Open the customer handbook
          </Button>
        </Link>
      </section>
    </div>
  );
}
